import { ElementRulesEditorViewComponentType } from "@graffer-inc/form-builder";
import {
  InputElementUnion,
  isInputElement,
  CommonInputSchemaValidator,
  ITextAreaElement,
  ITextInputElement,
  TextInputSchemaValidator,
  TextAreaSchemaValidator,
  isTextInputElement,
  isTextAreaElement,
} from "@graffer-inc/form-schema";
import { ApplicantConditionalRuleEditor } from "./applicant-conditional-rule-editor";
import { replaceElem } from "../../../../../../utils/array-helper";
import {
  ApplicantSchemaKeys,
  ReservedApplicantIdHelper,
} from "@graffer-inc/cloud-gov-core";
import { useMemo } from "react";
import { ExternalLink, MarkdownEditor } from "@graffer-inc/console-design-kit";
import {
  markdownHelpLink,
  grafferSchemasSuportTagNames,
} from "../../../../../../utils/markdown/constants";

export const ApplicantElementEditorViewFactory = (
  applicantSchemaKeys: ApplicantSchemaKeys,
  isSafeEditOnly: boolean,
): ElementRulesEditorViewComponentType => {
  const ApplicantElementEditorView: ElementRulesEditorViewComponentType = ({
    schemaBuilder,
    editState,
    onStateUpdate,
  }) => {
    const element = editState.element;

    // TODO(yo-tak): 現状、ここに入るelementは全てInputElementなのでこの分岐の中に入ることはないため、
    // 実質type assertionとしてのみ機能している。
    // 将来的には、申請者セクションの編集（セクション名の変更）、法人検索コンポーネント対応が見込まれるので、
    // その際にはISectionElement, IFunctionElement用のEditorViewを用意する。
    const commonSchemaValidator = useMemo(() => {
      if (isInputElement(element)) {
        return new CommonInputSchemaValidator(element);
      }
      return null;
    }, [element]);
    if (!isInputElement(element)) {
      return null;
    }

    // basically copy from CommonPropertiesEditor:
    // https://github.com/graffer-inc/graffer-schemas/blob/68b065a571187c16131248356ea2d62c37b1dcd4/libs/packages/form-builder/lib/elements/editor-pane/element-editor/editors/common-properties-editor.tsx#L15
    const descriptionId = `${element.id}-description`;

    const isShowPlaceholderEdit =
      // form-builderにおいてプレースホルダーの編集が可能なのは
      // TextInputElementとTextAreaElementのみである
      (isTextInputElement(element) || isTextAreaElement(element)) &&
      // 郵便番号はPostalCodeInputへ置き換え予定: https://graffer.esa.io/posts/3026
      !ReservedApplicantIdHelper.isApplicantPostalCode(
        element.id,
        applicantSchemaKeys,
      ) &&
      // 電話番号はform-builderに合わせてTelInputへ置き換える可能性があることを考慮し、
      // placeholderは編集不可とする。
      !ReservedApplicantIdHelper.isApplicantPhoneNumber(
        element.id,
        applicantSchemaKeys,
      );
    const isApplicantEmail = ReservedApplicantIdHelper.isApplicantEmail(
      element.id,
      applicantSchemaKeys,
    );
    const isApplicantPhoneNumber =
      ReservedApplicantIdHelper.isApplicantPhoneNumber(
        element.id,
        applicantSchemaKeys,
      );

    return (
      <>
        {isApplicantEmail && (
          <div className="px-3 is-size-7 has-text-grey mb-2">
            申請者が利用する際には、ログインやメール認証に利用したメールアドレスが自動で補完されます。
          </div>
        )}
        {isApplicantPhoneNumber && (
          <div className="px-3 is-size-7 has-text-grey mb-2">
            この項目では全角・半角、ハイフン有り・無しのいずれの値も受け付けられます。入力値を制御したい場合には、入力できる文字の種類を「説明文」に記載することで利用者の行動を促します。もしくは本項目を「非表示」にしたうえで、申請フォームの2ページ目以降で「1行テキスト」を選択して、正規表現の設定で入力できる文字を制御します。
          </div>
        )}
        <div className="px-3 py-3">
          <div className="field">
            <div className="label is-flex is-justify-content-space-between is-align-items-baseline mb-0">
              <label className="mb-2" htmlFor={descriptionId}>
                説明文
              </label>
              {element.description.isMarkdown && (
                <ExternalLink
                  anchor={(props) => (
                    <a
                      {...props}
                      href={markdownHelpLink}
                      target={"_blank"}
                      rel={"noopener noreferrer"}
                    >
                      {props.children}
                    </a>
                  )}
                >
                  入力方法やサンプルはこちら
                </ExternalLink>
              )}
            </div>
            {isApplicantPhoneNumber && (
              <div className="is-size-7 has-text-grey mb-2">
                例）日中に連絡が取れる電話番号を、ハイフン（-）なしの半角数字で入力してください
              </div>
            )}

            <div className="control">
              {element.description.isMarkdown ? (
                <MarkdownEditor
                  value={element.description.text}
                  layout="standalone"
                  allowedTagNames={grafferSchemasSuportTagNames}
                  editAreaOptions={{
                    attributes: {
                      id: descriptionId,
                      placeholder: "未設定",
                      rows: 5,
                    },
                  }}
                  onChange={(text) => {
                    const updated = {
                      ...element,
                      description: { ...element.description, text },
                    };
                    onStateUpdate({ ...editState, element: updated });
                  }}
                />
              ) : (
                <textarea
                  id={descriptionId}
                  className="textarea"
                  placeholder="未設定"
                  value={element.description.text}
                  onChange={(e) => {
                    const updated = {
                      ...element,
                      description: {
                        ...element.description,
                        text: e.target.value,
                      },
                    };
                    onStateUpdate({ ...editState, element: updated });
                  }}
                />
              )}
            </div>
            {/** GCDKのエラーメッセージコンポーネントを利用したいが、外部にはexportされていないため自前で記述する */}
            {commonSchemaValidator?.description() && (
              <p className="help is-danger">
                {commonSchemaValidator.description()}
              </p>
            )}
          </div>
          {isShowPlaceholderEdit && (
            <PlaceholderEditor
              element={element}
              onChange={(placeholder) => {
                const updated = {
                  ...element,
                  placeholder: placeholder,
                };
                onStateUpdate({ ...editState, element: updated });
              }}
            />
          )}
        </div>

        <hr className="mt-0 mb-5" />

        {isApplicantEmail && (
          <div className="notification is-link is-light">
            <div>
              <span className={`icon has-text-info`}>
                <i className="fas fa-exclamation-triangle" />
              </span>
              「メールアドレス」項目を非表示にした場合、申請者側の申請トップページにおいて
              <br />
              「アカウントでログインして申請に進む」の手段しか提示されなくなります。
            </div>
          </div>
        )}

        {editState.conditionalRules.map((r, index) => (
          <ApplicantConditionalRuleEditor
            formSchema={schemaBuilder.currentRawSchema}
            editingElement={editState.element as InputElementUnion}
            rule={r.rule}
            ruleIndex={r.index}
            onUpdate={(newRule) => {
              onStateUpdate({
                ...editState,
                conditionalRules: replaceElem(
                  editState.conditionalRules,
                  index,
                  {
                    ...r,
                    rule: newRule,
                  },
                ),
              });
            }}
            thenDisplayFilter={(rule) => rule.id === editState.element.id}
            applicantSchemaKeys={applicantSchemaKeys}
            isSafeEditOnly={isSafeEditOnly}
          />
        ))}
      </>
    );
  };

  return ApplicantElementEditorView;
};

const PlaceholderEditor: React.FC<{
  element: ITextInputElement | ITextAreaElement;
  onChange: (placeholder: string) => void;
}> = ({ element, onChange }) => {
  const validator = useMemo(() => {
    switch (element.inputType) {
      case "text":
        return new TextInputSchemaValidator(element);
      case "text_area":
        return new TextAreaSchemaValidator(element);
    }
  }, [element]);
  return (
    <div className="field">
      <label className="label">プレースホルダー</label>
      <div className="control">
        <input
          className="input"
          placeholder={element.placeholder}
          value={element.placeholder}
          onChange={(e) => onChange(e.target.value)}
        />
        {validator.placeholder() && (
          <p className="help is-danger">{validator.placeholder()}</p>
        )}
      </div>
    </div>
  );
};
